import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import { isNativeIOS, getIAPProducts } from '../../services/iap';
import { useAuth } from '../../context/AuthContext';
import { X, Check, Crown, Zap, RefreshCw, LogIn } from 'lucide-react';
import { Button } from '../common';

const CONTEXT_MESSAGES = {
  recipe_limit: "You've reached the free recipe limit",
  mealplan_week: 'Navigate to any week with Premium',
  cooking_mode: 'Unlock unlimited cooking sessions',
  grocery_list: 'Create unlimited grocery lists',
  category_limit: 'Add more custom categories',
  default: 'Unlock the full Cooksmith experience',
};

const PREMIUM_FEATURES = [
  'Unlimited recipes',
  'Full meal plan history',
  'Unlimited cooking mode sessions',
  'Multiple grocery lists',
  'Unlimited custom categories',
  'Priority support',
];

export default function UpgradeModal() {
  const {
    showUpgradeModal, setShowUpgradeModal, upgradeContext, startIapPurchase,
    purchasing, purchaseError, setPurchaseError, iapReady, iapError, retryIAP,
  } = useSubscription();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [appleProducts, setAppleProducts] = useState(null);

  useEffect(() => {
    if (showUpgradeModal && isNativeIOS()) {
      const products = getIAPProducts();
      if (products && products.length > 0) {
        const mapped = {};
        products.forEach((p) => {
          if (p.productId.includes('monthly')) mapped.monthly = p;
          if (p.productId.includes('yearly')) mapped.annual = p;
        });
        setAppleProducts(mapped);
      }
    }
  }, [showUpgradeModal]);

  if (!showUpgradeModal) return null;

  const contextMessage = CONTEXT_MESSAGES[upgradeContext] || CONTEXT_MESSAGES.default;
  const monthlyPrice = appleProducts?.monthly?.localizedPrice || '$4.99';
  const annualPrice = appleProducts?.annual?.localizedPrice || '$39.99';
  const annualMonthly = appleProducts?.annual
    ? `${(parseFloat(appleProducts.annual.price) / 12).toFixed(2)}/month`
    : '$3.33/month';

  const handleUpgrade = () => {
    setPurchaseError(null);
    startIapPurchase(selectedPlan);
  };

  const planButton = (plan, label, price, period, badge, monthlyEquiv) => {
    const selected = selectedPlan === plan;
    return (
      <button
        type="button"
        onClick={() => setSelectedPlan(plan)}
        aria-pressed={selected}
        className={[
          'relative w-full text-left p-4 rounded-xl border-2 transition-colors',
          selected
            ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
            : 'border-gray-200 dark:border-night-card hover:border-gray-300 dark:hover:border-night-muted',
        ].join(' ')}
      >
        {badge && (
          <span className="absolute -top-2 right-3 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary-600 text-white">
            {badge}
          </span>
        )}
        <div className="text-sm font-semibold text-gray-900 dark:text-night-text">{label}</div>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-2xl font-bold text-gray-900 dark:text-night-text">{price}</span>
          <span className="text-sm text-gray-500 dark:text-night-muted">/{period}</span>
        </div>
        {monthlyEquiv && <div className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">{monthlyEquiv}</div>}
        <div className="text-[11px] text-gray-500 dark:text-night-muted mt-1">
          Auto-renewable {period === 'month' ? 'monthly' : 'yearly'} subscription
        </div>
      </button>
    );
  };

  return (
    <div
      role="presentation"
      onClick={() => setShowUpgradeModal(false)}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 safe-area-top safe-area-bottom"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-title"
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white dark:bg-night-surface rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto"
      >
        <button
          onClick={() => setShowUpgradeModal(false)}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-500 dark:text-night-muted"
        >
          <X size={20} />
        </button>

        <div className="px-5 pt-6 pb-4 text-center">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mb-3" aria-hidden="true">
            <Crown size={28} />
          </div>
          <h3 id="upgrade-title" className="text-xl font-bold text-gray-900 dark:text-night-text">Upgrade to Premium</h3>
          <p className="text-sm text-gray-500 dark:text-night-muted mt-1">{contextMessage}</p>
        </div>

        <div className="px-5 space-y-3">
          {planButton('monthly', 'Premium Monthly', monthlyPrice, 'month')}
          {planButton('annual', 'Premium Yearly', annualPrice, 'year', 'Save 33%', annualMonthly)}
        </div>

        <ul className="px-5 mt-5 space-y-2">
          {PREMIUM_FEATURES.map((feature) => (
            <li key={feature} className="flex items-center gap-2 text-sm text-gray-700 dark:text-night-text">
              <Check size={16} className="text-emerald-500 flex-shrink-0" aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {purchaseError && (
          <p className="mx-5 mt-4 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
            {purchaseError}
          </p>
        )}

        <div className="px-5 mt-5 pb-5 space-y-3">
          {!isAuthenticated ? (
            <>
              <p className="text-sm text-gray-500 dark:text-night-muted text-center">
                Sign in to your account to subscribe.
              </p>
              <Button
                onClick={() => { setShowUpgradeModal(false); navigate('/auth/login'); }}
                className="w-full"
              >
                <LogIn size={18} aria-hidden="true" />
                Sign In to Subscribe
              </Button>
            </>
          ) : !isNativeIOS() ? (
            <p className="text-sm text-gray-600 dark:text-night-muted text-center bg-gray-50 dark:bg-night-card rounded-lg px-3 py-3">
              Premium subscriptions are available through the Cooksmith iOS app.
              Download Cooksmith from the App Store to upgrade — your Premium
              benefits will apply everywhere you sign in.
            </p>
          ) : (
            <>
              {!iapReady && iapError && (
                <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 text-center">
                  <p className="mb-2">Unable to load subscriptions from the App Store.</p>
                  <Button variant="secondary" onClick={retryIAP} className="min-h-0 py-1 px-3 text-xs">
                    <RefreshCw size={14} aria-hidden="true" /> Retry
                  </Button>
                </div>
              )}
              {!iapReady && !iapError && (
                <p className="text-sm text-gray-500 dark:text-night-muted text-center">
                  Loading subscriptions from the App Store…
                </p>
              )}
              <Button
                onClick={handleUpgrade}
                disabled={purchasing || !iapReady}
                className="w-full"
              >
                {purchasing ? 'Processing…' : (
                  <>
                    <Zap size={18} aria-hidden="true" />
                    Upgrade Now
                  </>
                )}
              </Button>
            </>
          )}

          <p className="text-[11px] text-center text-gray-500 dark:text-night-muted">
            <button
              type="button"
              onClick={() => { setShowUpgradeModal(false); navigate('/terms'); }}
              className="underline"
            >
              Terms of Use (EULA)
            </button>
            {' · '}
            <button
              type="button"
              onClick={() => { setShowUpgradeModal(false); navigate('/privacy'); }}
              className="underline"
            >
              Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
